import type { TokenPair } from '@tasks/contracts/auth';
import type { TabChannel } from './tab-channel.js';
import type { TokenStore } from './token-store.js';

export type RefreshOutcome =
  | { status: 'refreshed'; pair: TokenPair }
  | { status: 'adopted' }
  | { status: 'signed-out' }
  | { status: 'offline' };

export type RefreshCall = (refreshToken: string) => Promise<TokenPair>;

export type CoordinatorDeps = {
  store: TokenStore;
  channel: TabChannel;
  call: RefreshCall;
  /** Inyectable para poder probar el camino sin Web Locks. */
  lock: <T>(fn: () => Promise<T>) => Promise<T>;
  /** The pause before the one grace retry (see `callWithGraceRetry`); injectable so a test does not sleep. */
  wait?: (ms: number) => Promise<void>;
};

/** Un 401 del propio /auth/refresh: el token ya no sirve y hay que volver a login. `type` is the problem type the server sent. */
export class RefreshRejected extends Error {
  constructor(
    message?: string,
    readonly type?: string,
  ) {
    super(message);
  }
}

/** What the server answers the SIMULTANEOUS loser of a refresh — the session is alive (`refresh-failure.ts`, `replay`). */
export const TOKEN_INVALID = 'https://tasks/errors/token-invalid';
/** Long enough for the winner to write its grace pair (`refresh-rotation.ts`: consume → issue → rememberRefresh). */
export const GRACE_RETRY_MS = 300;

/**
 * Un solo refresh en vuelo por pestaña y uno por navegador gracias al lock. Dentro del lock
 * se relee `localStorage`: si otra pestaña ya rotó, se adopta su par en vez de gastar el token
 * viejo, que es lo que provocaría una detección de reuso y echaría a todo el mundo (diseño §4).
 */
export class RefreshCoordinator {
  private inFlight: Promise<RefreshOutcome> | null = null;

  constructor(private readonly deps: CoordinatorDeps) {}

  run(): Promise<RefreshOutcome> {
    this.inFlight ??= this.execute().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  private async execute(): Promise<RefreshOutcome> {
    const before = this.deps.store.refresh;
    if (before === null) return { status: 'signed-out' };
    return this.deps.lock(() => this.rotate(before));
  }

  private async rotate(before: string): Promise<RefreshOutcome> {
    // Relectura dentro del lock: mientras esperábamos, otra pestaña pudo rotar.
    const current = this.deps.store.refresh;
    if (current === null) return { status: 'signed-out' };
    // Another tab rotated while this one waited. Its pair reaches this tab only through the broadcast, and the access token lives only in
    // memory: a tab holding NO access (freshly loaded) that missed that message would be «adopted» into a session without a token —
    // authenticated, no timer, every request a bearer-less 401 that nothing renews (FILA-TASKS-AUTH-TWO-TABS-REFRESH-1, the red of tasks
    // PR 112). So it adopts only when it has an access of its own; otherwise it rotates the CURRENT token — still inside the lock, under the
    // same compare-and-set below, never the one it read before — and the other tab picks the new pair up from this tab's broadcast.
    if (current !== before && this.deps.store.access !== null) return { status: 'adopted' };

    try {
      const pair = await this.callWithGraceRetry(current);
      // Compare-and-set: the answer is only good for the refresh token it was asked with. If the store holds something else NOW — `accept()` (a
      // fresh login or a door) replaced it, another tab rotated, or a sign-out cleared it — the late pair is dropped: it must not land on top of the
      // newer session nor resurrect a signed-out one (T1 of GABO on U2b-2, O1).
      const moved = this.supersededBy(current);
      if (moved !== null) return moved;
      this.deps.store.save(pair);
      this.deps.channel.post({ kind: 'rotated', pair });
      return { status: 'refreshed', pair };
    } catch (error) {
      return this.onFailure(error, current);
    }
  }

  /**
   * P2 (FILA-TASKS-AUTH-TWO-TABS-REFRESH-1): a 401 `token-invalid` is what the server answers the SIMULTANEOUS loser of a refresh — the
   * winner consumed the token but has not yet written the grace pair, and the session is NOT revoked (`auth.integration.spec.ts`). Read as
   * death it signed every tab out: a reload in the middle of a refresh, the other tab spending the same token 47–78 ms later (the two-tabs
   * e2e under load, 2/30). So that one answer earns ONE retry of the same token, still inside the lock, after a pause by which the grace
   * cache replays the pair. A second 401, or `token-reuse` / `session-expired`, is the death it always was.
   */
  private async callWithGraceRetry(token: string): Promise<TokenPair> {
    try {
      return await this.deps.call(token);
    } catch (error) {
      if (!(error instanceof RefreshRejected) || error.type !== TOKEN_INVALID) throw error;
      const wait =
        this.deps.wait ?? ((ms: number) => new Promise<void>((done) => setTimeout(done, ms)));
      await wait(GRACE_RETRY_MS);
      return this.deps.call(token);
    }
  }

  /** What replaced the refresh token this call spent while it was in flight, or null if it is still the one in the store. */
  private supersededBy(spent: string): RefreshOutcome | null {
    const now = this.deps.store.refresh;
    if (now === spent) return null;
    return now === null ? { status: 'signed-out' } : { status: 'adopted' };
  }

  /** Solo un rechazo del servidor borra el refresh; un fallo de red lo conserva. A late failure about a token that is no longer the stored one changes nothing. */
  private onFailure(error: unknown, spent: string): RefreshOutcome {
    const moved = this.supersededBy(spent);
    if (moved !== null) return moved;
    if (error instanceof RefreshRejected) {
      this.deps.store.clear();
      this.deps.channel.post({ kind: 'signed-out' });
      return { status: 'signed-out' };
    }
    this.deps.store.clearAccess();
    return { status: 'offline' };
  }
}
